"use client";

import Link from "next/link";
import { useState } from "react";
import { usePathname } from "next/navigation";
import { site } from "@/lib/site";
import { footerLocations, type FooterCity } from "@/lib/footerLocations";
import { Container } from "./Container";

// Footer replicates the LIVE site (tablespace.com): Follow social row,
// Solutions / Company / Support / Contact Us columns, and the expandable
// "Locations & Cities" centre directory. Tagline text matches the live
// tagline graphic ("Flexible Workspace Solutions for Modern Teams").
const cols = [
  {
    title: "Solutions",
    links: [
      { label: "Custom Built (Managed Offices)", href: "/solutions/custom-built" },
      { label: "Suites", href: "/solutions/suites" },
      { label: "Enterprise Services", href: "/solutions/enterprise-services" },
      { label: "Desyn", href: "/solutions/desyn" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Us", href: "/about" },
      { label: "Investor Relations", href: "/investor-relations" },
      { label: "Technology", href: "/technology" },
      { label: "Careers", href: "/careers" },
      { label: "Legal", href: "/legal" },
      { label: "Media", href: "/media" },
      { label: "Blogs", href: "/blog" },
      { label: "Sustainability", href: "/sustainability" },
      { label: "Sitemap", href: "/sitemap" },
    ],
  },
  {
    title: "Support",
    links: [{ label: "Contact", href: "/contact-us" }],
  },
];

// Social profiles, as on the live footer.
const socials = [
  {
    platform: "linkedin",
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/tablespaceofficial/",
    path: "M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zm1.78 13.02H3.55V9h3.57v11.45zM22.23 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0h.01z",
  },
  {
    platform: "instagram",
    label: "Instagram",
    href: "https://www.instagram.com/tablespaceofficial/",
    path: "M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.35 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.35-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.72 3.72 0 0 1-1.38-.9 3.72 3.72 0 0 1-.9-1.38c-.16-.42-.35-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.35 2.23-.41 1.27-.06 1.65-.07 4.85-.07M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63a5.88 5.88 0 0 0-2.13 1.38A5.88 5.88 0 0 0 .63 4.14C.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.8.72 1.48 1.38 2.13a5.88 5.88 0 0 0 2.13 1.38c.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.88 5.88 0 0 0 2.13-1.38 5.88 5.88 0 0 0 1.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.88 5.88 0 0 0-1.38-2.13A5.88 5.88 0 0 0 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84zm0 10.15A4 4 0 1 1 16 12a4 4 0 0 1-4 3.99zm7.85-10.4a1.44 1.44 0 1 1-2.88 0 1.44 1.44 0 0 1 2.88 0z",
  },
  {
    platform: "facebook",
    label: "Facebook",
    href: "https://www.facebook.com/TableSpaceOfficial",
    path: "M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.02 4.39 11.02 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.7 4.53-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z",
  },
  {
    platform: "x",
    label: "X",
    href: "https://x.com/Table_Space_",
    path: "M18.24 2.25h3.31l-7.23 8.26L22.83 21.75h-6.66l-5.22-6.82-5.97 6.82H1.67l7.73-8.84L1.25 2.25h6.83l4.72 6.23 5.44-6.23zm-1.16 17.52h1.83L7.08 4.13H5.12l11.96 15.64z",
  },
  {
    platform: "youtube",
    label: "YouTube",
    href: "https://youtube.com/@tablespaceofficial",
    path: "M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.5A3.02 3.02 0 0 0 .5 6.19C0 8.07 0 12 0 12s0 3.93.5 5.81a3.02 3.02 0 0 0 2.12 2.14c1.88.5 9.38.5 9.38.5s7.5 0 9.38-.5a3.02 3.02 0 0 0 2.12-2.14C24 15.93 24 12 24 12s0-3.93-.5-5.81zM9.55 15.57V8.43L15.82 12l-6.27 3.57z",
  },
];

export function Footer({
  socialLinks,
  c = {},
  locations = footerLocations,
}: {
  socialLinks?: { platform: string; url: string; enabled: boolean }[];
  /** Footer copy overlay (admin-editable via /admin/pages → Footer). */
  c?: Record<string, string>;
  /** City → micro-market → centre tree from /admin/locations (lib/locationsTree);
   * the static lib/footerLocations snapshot is the no-DB fallback. */
  locations?: FooterCity[];
} = {}) {
  // Merge the admin-config links (URL + on/off) with the icon SVG paths;
  // fall back to the built-in defaults when no config is supplied.
  const socialItems = socials
    .map((s) => {
      const cfg = socialLinks?.find((l) => l.platform === s.platform);
      if (cfg && !cfg.enabled) return null;
      return { ...s, href: cfg?.url || s.href };
    })
    .filter(Boolean) as typeof socials;

  // which city's centre list is expanded in "Locations & Cities"
  const [openCity, setOpenCity] = useState<string | null>(null);
  // Micro-markets are collapsible too — keyed "city|market". Switching city
  // resets the open market.
  const [openMarket, setOpenMarket] = useState<string | null>(null);
  const pathname = usePathname();

  // The admin console has its own chrome (app/admin/layout) — no site footer.
  if (pathname.startsWith("/admin")) return null;

  return (
    <footer className="bg-brand-dark text-white">
      <Container className="pt-20 pb-10">
        {/* logo + tagline graphic ("WORK REIMAGINED AND WORKSPACE REIMAGINED"),
            exactly as on the live site footer */}
        <div className="flex flex-col justify-between gap-8 border-b border-white/15 pb-10 md:flex-row md:items-end">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={c["tagline.image"]?.trim() || "/images/tablespace-tagline-logo-white.webp"}
            alt={c["tagline.image.alt"]?.trim() || "Table Space — Work Reimagined and Workspace Reimagined"}
            data-cms-img="tagline.image"
            className="h-auto w-64 md:w-80"
          />
          <div className="flex items-center gap-3">
            {socialItems.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Follow Table Space on ${s.label}`}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white/70 transition-colors hover:border-brand-accent-bright hover:text-brand-accent-bright"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d={s.path} />
                </svg>
              </a>
            ))}
          </div>
        </div>

        {/* directory — matches the live site footer, plus the registered
            office block required alongside Contact Us */}
        <div className="grid grid-cols-2 gap-10 py-14 md:grid-cols-3 lg:grid-cols-6">
          {cols.map((col, i) => (
            <div key={col.title}>
              <h4 className="text-base font-medium text-white" data-cms-key={`col.${i}.title`}>
                {c[`col.${i}.title`]?.trim() || col.title}
              </h4>
              <ul className="mt-5 space-y-3">
                {col.links.map((l, j) => (
                  <li key={l.label}>
                    <Link
                      href={l.href}
                      className="text-sm text-white/65 transition-colors hover:text-white"
                    >
                      <span data-cms-key={`col.${i}.link.${j}.label`}>
                        {c[`col.${i}.link.${j}.label`]?.trim() || l.label}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact us */}
          <div>
            <h4 className="text-base font-medium text-white" data-cms-key="contact.title">
              {c["contact.title"]?.trim() || "Contact Us"}
            </h4>
            <ul className="mt-5 space-y-3 text-sm text-white/65">
              <li>
                <a
                  href={`tel:${(c["contact.phone"]?.trim() || site.contact.phone).replace(/\s/g, "")}`}
                  className="transition-colors hover:text-white"
                >
                  <span data-cms-key="contact.phone">{c["contact.phone"]?.trim() || site.contact.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${c["contact.email"]?.trim() || site.contact.email}`}
                  className="transition-colors hover:text-white"
                >
                  <span data-cms-key="contact.email">{c["contact.email"]?.trim() || site.contact.email}</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Registered office — the statutory entity name, CIN and address.
              Wider than the other columns so the CIN keeps to one line. */}
          <div className="lg:col-span-2">
            <h4 className="text-base font-medium text-white" data-cms-key="registered.title">
              {c["registered.title"]?.trim() || "Registered Office"}
            </h4>
            <ul className="mt-5 space-y-3 text-sm text-white/65">
              <li data-cms-key="registered.name">
                {c["registered.name"]?.trim() || site.registeredOffice.name}
              </li>
              <li className="whitespace-nowrap" data-cms-key="registered.cin">
                {c["registered.cin"]?.trim() || `CIN: ${site.registeredOffice.cin}`}
              </li>
              <li className="leading-relaxed text-white/55" data-cms-key="registered.address">
                {c["registered.address"]?.trim() || site.registeredOffice.address}
              </li>
            </ul>
          </div>
        </div>

        {/* Locations & Cities — expandable directory replicated from the live
            site: city -> micro-market submenus -> centre pages */}
        <div className="border-t border-white/15 py-8">
          <h4 className="text-base font-medium text-white">Locations &amp; Cities</h4>
          <div className="mt-4 grid grid-cols-1 items-start gap-x-10 sm:grid-cols-2 lg:grid-cols-4">
            {locations.map((c) => {
              const isOpen = openCity === c.name;
              return (
                <div key={c.name} className="border-b border-white/10">
                  <button
                    type="button"
                    onClick={() => { setOpenCity(isOpen ? null : c.name); setOpenMarket(null); }}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-3 py-3 text-left text-sm text-white/75 transition-colors hover:text-white"
                  >
                    Workspace in {c.name}
                    <svg
                      className={`h-3 w-3 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      aria-hidden
                    >
                      <path d="m3 4.5 3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  {isOpen && (
                    <div className="space-y-1 pb-4">
                      {c.markets.map((m) => {
                        const marketKey = `${c.name}|${m.name}`;
                        const marketOpen = openMarket === marketKey;
                        return (
                          <div key={m.name}>
                            {m.name ? (
                              <button
                                type="button"
                                onClick={() => setOpenMarket(marketOpen ? null : marketKey)}
                                aria-expanded={marketOpen}
                                className="flex w-full items-center justify-between gap-3 py-1.5 text-left mono-meta text-[0.65rem] uppercase tracking-[0.14em] text-brand-accent-bright transition-colors hover:text-white"
                              >
                                {m.name}
                                <svg
                                  className={`h-2.5 w-2.5 shrink-0 transition-transform duration-300 ${marketOpen ? "rotate-180" : ""}`}
                                  viewBox="0 0 12 12"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  aria-hidden
                                >
                                  <path d="m3 4.5 3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                            ) : null}
                            {(marketOpen || !m.name) && (
                              <ul className="mt-1 space-y-1.5 pb-2 pl-2">
                                {m.centres.map((ct) => (
                                  <li key={ct.href}>
                                    <a
                                      href={ct.href}
                                      className="text-[0.8rem] leading-snug text-white/50 transition-colors hover:text-brand-accent-bright"
                                    >
                                      {ct.name}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* legal */}
        <div className="flex flex-col gap-3 border-t border-white/15 pt-8 mono-meta text-white/45 sm:flex-row sm:items-center sm:justify-between">
          {/* The year is generated, so it can never go stale; the entity name
              and the trailing phrase are both editable. A "copyright" field was
              registered in the admin long before this line read it — saving it
              did nothing, which is why the footer looked uneditable. */}
          <span>
            © {new Date().getFullYear()}{" "}
            <span data-cms-key="copyright">{c["copyright"]?.trim() || site.legalName}</span>
            . <span data-cms-key="copyright.suffix">{c["copyright.suffix"]?.trim() || "All rights reserved."}</span>
          </span>
          <span className="flex gap-5">
            <Link href="/legal" className="hover:text-white">Legal</Link>
            <Link href="/privacy-policy" className="hover:text-white">Privacy</Link>
            <Link href="/terms-and-conditions" className="hover:text-white">Terms</Link>
          </span>
        </div>
      </Container>
    </footer>
  );
}
